import React from 'react';
import {
  CasNumbersCell,
  CategoriesCell,
  PesticideGroupsCell,
  ChemicalFamiliesCell,
  ResistanceRiskCell,
  RiskSymbolCell,
  ActionLinkCell,
  TagsCell,
} from '@client/components/ui/table/TableCells';
import TruncatedText from '@client/components/ui/TruncatedText';


/**
 * Factory function for expanded row rendering
 *
 * Creates a render function that displays additional record information
 * in an expandable row format using organized tag groups.
 *
 * @returns {Function} Expanded row render function
 *
 * @example
 * const expandedRowRender = createExpandedRowRender();
 * <Table expandable={{ expandedRowRender }} />
 */
// eslint-disable-next-line react/display-name
export const createExpandedRowRender = () => (record) => {
  // eslint-disable-next-line no-undef
  const uniqueItems = new Map();

  const addUnique = (id, name) => {
    if (name && !uniqueItems.has(id)) {
      uniqueItems.set(id, name);
    }
  };

  record.activity_sectors?.forEach((sector) => {
    addUnique(sector.id, sector.name);
  });

  record.cultures?.forEach((culture) => {
    addUnique(culture.id, culture.french_name);

    culture.cultural_practices?.forEach((practice) => {
      addUnique(practice.id, practice.name);
    });
  });

  record.pesticide_types?.forEach((type) => {
    addUnique(type.id, type.label);
  });

  const tagGroups = Array.from(uniqueItems.entries())
    .slice(0, 20)
    .map(([id, name]) => ({
      key: id,
      name: name,
    }));

  return (
    <div className="expanded-row">
      <TagsCell
        items={tagGroups}
        getItemName={(item) => item.name}
        getItemKey={(item) => item.key}
      />
    </div>
  );
};

/**
 * Complete table columns configuration for active substances
 *
 * Defines all columns including their rendering, sorting behavior,
 * and display properties. Supports both server-side and client-side sorting.
 *
 * @param {Function} t - Internationalization function
 * @param {Object} currentSorting - Current sorting state
 * @param {string} currentSorting.field - Currently sorted field
 * @param {string} currentSorting.order - Current sort order ('ascend'|'descend')
 * @returns {Array} Complete Antd Table columns configuration
 *
 * @example
 * const columns = createTableColumns(t, { field: 'name', order: 'ascend' });
 * <Table columns={columns} />
 */
export const createTableColumns = (t, currentSorting) => [
  {
    title: t('active-substance|table.Active substance name'),
    dataIndex: 'name',
    key: 'name',
    render: (text, record) => (
      <a
        href={`/matieres-actives/details/${record.id}`}
        target="_blank"
        rel="noreferrer"
        className="text-[#105235] hover:text-[#105235] !font-[700] underline text-[12px]"
      >
        <TruncatedText text={text} maxLength={35} />
      </a>
    ),
    sorter: true,
    sortOrder: currentSorting?.field === 'name' ? currentSorting.order : null,
  },
  {
    title: t('active-substance|table.CAS number'),
    dataIndex: 'cas_numbers',
    key: 'cas_numbers',
    render: (casNumbers) => <CasNumbersCell casNumbers={casNumbers} />,
    sorter: true,
    sortOrder:
      currentSorting?.field === 'cas_numbers' ? currentSorting.order : null,
  },
  {
    title: t('active-substance|table.Pesticide category'),
    dataIndex: 'pesticide_categories',
    key: 'pesticide_categories',
    render: (categories) => <CategoriesCell categories={categories} />,
    sorter: true,
    sortOrder:
      currentSorting?.field === 'pesticide_categories'
        ? currentSorting.order
        : null,
  },
  {
    title: t('active-substance|table.Chemical or biological family'),
    key: 'chemical_families',
    dataIndex: 'pesticide_group_associations',
    render: (associations) => (
      <ChemicalFamiliesCell associations={associations} />
    ),
    sorter: { compare: () => 0 },
    sortDirections: ['ascend', 'descend'],
    sortOrder:
      currentSorting?.field === 'chemical_families'
        ? currentSorting.order
        : null,
  },
  {
    title: t('active-substance|table.Pesticide groups'),
    dataIndex: 'pesticide_group_associations',
    key: 'pesticide_groups',
    render: (associations) => (
      <PesticideGroupsCell associations={associations} />
    ),
    sorter: { compare: () => 0 },
    sortDirections: ['ascend', 'descend'],
    sortOrder:
      currentSorting?.field === 'pesticide_groups'
        ? currentSorting.order
        : null,
  },
  {
    title: t('active-substance|table.Resistance development risk'),
    dataIndex: 'pesticide_group_associations',
    key: 'resistance_risk',
    render: (associations) => (
      <ResistanceRiskCell associations={associations} />
    ),
    sorter: { compare: () => 0 },
    sortDirections: ['ascend', 'descend'],
    sortOrder:
      currentSorting?.field === 'resistance_risk' ? currentSorting.order : null,
  },
  {
    title: t('active-substance|table.Toxicity aigue'),
    key: 'acute_toxicity',
    className: 'vertical-cell',
    render: (_, record) => (
      <RiskSymbolCell
        symbolData={record.health?.acute_toxicity_symbol_data}
        alt="Acute toxicity"
      />
    ),
  },
  {
    title: t('active-substance|table.Long term effects'),
    key: 'long_term_effects',
    className: 'vertical-cell',
    render: (_, record) => (
      <RiskSymbolCell
        symbolData={record.health?.long_term_effect_symbol_data}
        alt="Long term effects"
      />
    ),
  },
  {
    title: t('active-substance|table.Fish or dophani'),
    key: 'fish_daphnia',
    className: 'vertical-cell',
    render: (_, record) => (
      <RiskSymbolCell
        symbolData={record.environment?.fishes_symbol_data}
        alt="Fish or daphnia"
      />
    ),
  },
  {
    title: t('active-substance|table.Birds'),
    key: 'birds',
    className: 'vertical-cell',
    render: (_, record) => (
      <RiskSymbolCell
        symbolData={record.environment?.birds_symbol_data}
        alt="Birds"
      />
    ),
  },
  {
    title: t('active-substance|table.Bees'),
    key: 'bees',
    className: 'vertical-cell',
    render: (_, record) => (
      <RiskSymbolCell
        symbolData={record.environment?.bees_symbol_data}
        alt="Bees"
      />
    ),
  },
  {
    title: t('active-substance|table.Persistance'),
    key: 'persistence',
    className: 'vertical-cell',
    render: (_, record) => (
      <RiskSymbolCell
        symbolData={record.environment?.persistence_symbol_data}
        alt="Persistence"
      />
    ),
  },
  {
    title: t('active-substance|table.Leaching potential'),
    key: 'leaching_potential',
    className: 'vertical-cell',
    render: (_, record) => (
      <RiskSymbolCell
        symbolData={record.environment?.leaching_symbol_data}
        alt="Leaching potential"
      />
    ),
  },
  {
    title: t('active-substance|table.Active substance file'),
    key: 'details',
    className: 'vertical-cell',
    render: (_, record) => (
      <ActionLinkCell
        href={`/matieres-actives/details/${record.id}`}
        target="_blank"
        className="btn btn--primary btn--sm"
      >
        {t('active-substance|table.See')}
      </ActionLinkCell>
    ),
  },
];
